import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { db } from '../db/AppDatabase';
import { IScanItem } from '../interfaces/IScanItem';
import { ExamSessionService } from '../_Services/apih/exam-session-service';
import { StudentsService } from './students-service';

@Injectable({
  providedIn: 'root',
})
export class ScanSyncService {
  private scanAPI = inject(ExamSessionService);
  private studentsService = inject(StudentsService);
  private isSyncing = false;
  private pendingSync = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  // Exponential backoff: 30s → 60s → 120s → … tot max 10 min
  private retryDelay = 30_000;
  private readonly maxRetryDelay = 600_000;

  /**
   * Registreer de window online-listener. Eenmalig aan te roepen bij app-opstart.
   */
  registerOnlineListener(): void {
    window.addEventListener('online', () => {
      this.retryDelay = 30_000; // netwerk terug: reset backoff
      this.cancelRetry();
      this.triggerSync();
    });
  }

  /**
   * Stuur alle niet-gesynchroniseerde scans in batch naar de API.
   * Fire-and-forget: gooit nooit een exceptie naar de aanroeper.
   * Wordt automatisch geskipt als er al een sync lopende is.
   */
  triggerSync(): void {
    if (this.isSyncing) {
      this.pendingSync = true;
      return;
    }
    this.isSyncing = true;
    this.syncPendingScans()
      .catch(() => { /* netwerk weg of API-fout — volgende trigger probeert opnieuw */ })
      .finally(() => {
        this.isSyncing = false;
        if (this.pendingSync) {
          this.pendingSync = false;
          this.triggerSync();
        }
      });
  }

  private scheduleRetry(): void {
    this.cancelRetry();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.triggerSync();
    }, this.retryDelay);
    // Verdubbel de wachttijd voor volgende poging (exponential backoff)
    this.retryDelay = Math.min(this.retryDelay * 2, this.maxRetryDelay);
  }

  private cancelRetry(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private async syncPendingScans(): Promise<void> {
    const unsynced = await db.scanHistory.filter(s => !s.synced).toArray();
    if (unsynced.length === 0) {
      this.cancelRetry();
      return;
    }

    // Groepeer per examen zodat we 1 API-call per examen kunnen doen
    const byExam = new Map<string, IScanItem[]>();
    for (const scan of unsynced) {
      const list = byExam.get(scan.examId) ?? [];
      list.push(scan);
      byExam.set(scan.examId, list);
    }

    let anyFailed = false;
    for (const [examId, scans] of byExam) {
      try {
        const scanRecords = await Promise.all(scans.map(async scan => ({
          appScanID: scan.id,  
          studentUserID: scan.studentId ?? '',
          scanDate: scan.date,
          scanDirection: scan.direction,
          scanStatus: scan.status,
          scanMethod: scan.method,
          scanCode: scan.scanCode,
          photoBase64: scan.photo ? await this.blobToDataUrl(scan.photo) : undefined,
          scannerName: scan.scannerName,
        })));

        await firstValueFrom(this.scanAPI.AddScansForExam({ examenSlotID: examId, scanRecords }));

        // Markeer de volledige batch als verstuurd
        const ids = scans.map(s => s.id);
        await db.scanHistory.where('id').anyOf(ids).modify({ synced: true });
        this.studentsService.markScansAsSynced(ids);
      } catch {
        // Server of netwerk onbeschikbaar — retry wordt ingepland
        anyFailed = true;
      }
    }

    if (anyFailed) {
      this.scheduleRetry(); // server was down: probeer opnieuw met backoff
    } else {
      this.retryDelay = 30_000; // alles gelukt: backoff resetten
      this.cancelRetry();
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onerror = () => reject(new Error('Failed to read blob'));
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(blob);
    });
  }
}
